import React, { useState } from 'react';
import { X, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

interface ExportColumn {
  key: string;
  label: string;
  checked: boolean;
}

interface ExportDeficienciesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (selectedColumns: string[]) => Promise<any[]>;
  exporting: boolean;
}

const defaultColumns: ExportColumn[] = [
  { key: 'deficiencyNo', label: 'Deficiency No', checked: true },
  { key: 'project', label: 'Project', checked: true },
  { key: 'unit', label: 'Unit', checked: true },
  { key: 'status', label: 'Status', checked: true },
  { key: 'priority', label: 'Priority', checked: true },
  { key: 'tags', label: 'Tags', checked: true },
  { key: 'deficiencyType', label: 'Deficiency Type', checked: true },
  { key: 'title', label: 'Title', checked: true },
  { key: 'reportedOn', label: 'Reported On', checked: true },
  { key: 'reportedBy', label: 'Reported By', checked: true },
  { key: 'dueDate', label: 'Due Date', checked: true },
  { key: 'locationCategory', label: 'Location /Category', checked: true },
  { key: 'assignee', label: 'Assignee', checked: true },
  { key: 'assignedBy', label: 'Assigned By', checked: true },
  { key: 'assignedDate', label: 'Assigned Date', checked: true },
  { key: 'completedBy', label: 'Completed By', checked: true },
  { key: 'completedDate', label: 'Completed Date', checked: true },
  { key: 'openBy', label: 'Open By', checked: true },
  { key: 'openDate', label: 'Open Date', checked: true },
  { key: 'closedBy', label: 'Closed By', checked: true },
  { key: 'closedDate', label: 'Closed Date', checked: true },
  { key: 'updatedBy', label: 'Updated By', checked: true },
  { key: 'updatedDate', label: 'Updated Date', checked: true },
  { key: 'rejected', label: 'Rejected', checked: true },
  { key: 'inspectionType', label: 'Inspection Type', checked: true }
];

const ExportDeficienciesModal: React.FC<ExportDeficienciesModalProps> = ({
  isOpen,
  onClose,
  onExport,
  exporting
}) => {
  const [columns, setColumns] = useState<ExportColumn[]>(defaultColumns);

  const handleColumnToggle = (key: string) => {
    setColumns(prev =>
      prev.map(col =>
        col.key === key ? { ...col, checked: !col.checked } : col
      )
    );
  };

  const handleSelectAll = () => {
    const allChecked = columns.every(col => col.checked);
    setColumns(prev => prev.map(col => ({ ...col, checked: !allChecked })));
  };

  const handleExport = async () => {
    const selectedColumns = columns.filter(col => col.checked).map(col => col.key);

    if (selectedColumns.length === 0) {
      return;
    }

    try {
      const data = await onExport(selectedColumns);

      if (data && data.length > 0) {
        // Create worksheet data with headers
        const headers = columns.filter(col => col.checked).map(col => col.label);
        const wsData = [headers];

        // Add data rows
        data.forEach(row => {
          const rowData = selectedColumns.map(key => {
            const value = row[key];
            if (value === null || value === undefined) return '';
            if (value instanceof Date) return value.toLocaleDateString();
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
          });
          wsData.push(rowData);
        });

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Set column widths
        const colWidths = headers.map(h => ({ wch: Math.max(h.length + 2, 15) }));
        ws['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(wb, ws, 'Deficiencies');

        // Generate filename with date
        const date = new Date().toISOString().split('T')[0];
        const filename = `deficiencies_export_${date}.xlsx`;

        // Download file
        XLSX.writeFile(wb, filename);
        onClose();
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (!isOpen) return null;

  // Split columns into two groups for two-column layout
  const midpoint = Math.ceil(columns.length / 2);
  const leftColumns = columns.slice(0, midpoint);
  const rightColumns = columns.slice(midpoint);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #E5E7EB'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#48182F' }}>
            Export Deficiencies
          </h2>
          <button
            onClick={onClose}
            disabled={exporting}
            style={{
              background: 'none',
              border: 'none',
              cursor: exporting ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Choose Column
            </h3>
            <button
              onClick={handleSelectAll}
              disabled={exporting}
              style={{
                fontSize: '13px',
                color: '#48182F',
                background: 'none',
                border: 'none',
                cursor: exporting ? 'not-allowed' : 'pointer',
                textDecoration: 'underline'
              }}
            >
              {columns.every(col => col.checked) ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '24px' }}>
            {/* Left Column */}
            <div style={{ flex: 1 }}>
              {leftColumns.map((column) => (
                <label
                  key={column.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 0',
                    cursor: exporting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: '#374151'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={column.checked}
                    onChange={() => handleColumnToggle(column.key)}
                    disabled={exporting}
                    style={{
                      width: '18px',
                      height: '18px',
                      accentColor: '#48182F',
                      cursor: exporting ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {column.label}
                </label>
              ))}
            </div>

            {/* Right Column */}
            <div style={{ flex: 1 }}>
              {rightColumns.map((column) => (
                <label
                  key={column.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 0',
                    cursor: exporting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: '#374151'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={column.checked}
                    onChange={() => handleColumnToggle(column.key)}
                    disabled={exporting}
                    style={{
                      width: '18px',
                      height: '18px',
                      accentColor: '#48182F',
                      cursor: exporting ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {column.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 20px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <button
            onClick={handleExport}
            disabled={exporting || columns.every(col => !col.checked)}
            className="btn btn-primary btn-md"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: exporting || columns.every(col => !col.checked) ? 0.7 : 1,
              cursor: exporting || columns.every(col => !col.checked) ? 'not-allowed' : 'pointer'
            }}
          >
            {exporting ? (
              <>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                Exporting...
              </>
            ) : (
              <>
                <Download size={16} />
                Export
              </>
            )}
          </button>
        </div>

        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default ExportDeficienciesModal;
